import { useMutation, type UseMutationOptions } from '@tanstack/react-query'
import { useRef } from 'react'

export class MutationGatedError extends Error {
  constructor() {
    super('Mutation is already in progress')
    this.name = 'MutationGatedError'
  }
}

/**
 * Wraps mutation options so only one call runs at a time: a second call
 * while one is in flight fails fast with MutationGatedError, which skips
 * the caller's onError. `gate` holds the in-flight flag.
 */
export function gateMutationOptions<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TOnMutateResult = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
  gate: { current: boolean },
): UseMutationOptions<TData, TError, TVariables, TOnMutateResult> {
  return {
    ...options,
    mutationFn: async (...args) => {
      if (gate.current) {
        throw new MutationGatedError()
      }
      gate.current = true
      return options.mutationFn!(...args)
    },
    onSettled: (...args) => {
      gate.current = false
      return options.onSettled?.(...args)
    },
    onError: (...args) => {
      if (args[0] instanceof MutationGatedError) return
      return options.onError?.(...args)
    },
  }
}

export function useGatedMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TOnMutateResult = unknown,
>(options: UseMutationOptions<TData, TError, TVariables, TOnMutateResult>) {
  const gateRef = useRef(false)

  return useMutation<TData, TError, TVariables, TOnMutateResult>(
    gateMutationOptions(options, gateRef),
  )
}
